/**
 * Meniul unei vitrine PENTRU ADRESA din sesiune, in doi pasi, plus
 * reverificarea cosului contra lui.
 *
 * Pasul 1 — previzualizarea pe adresa (`POST /storefront/:slug/adresa`): zona
 * (taxa, minim), restaurantul probabil si un timp orientativ. Raspunde la o
 * intrebare care nu depinde de cos, deci se cere o singura data per
 * (vitrina, adresa).
 *
 * Pasul 2 — meniul pentru zona (`GET /storefront/:slug?zonaId=…&lat&lng`):
 * aceleasi oferte, dar cu disponibilitatea si optiunile restaurantelor care
 * chiar ajung la punctul acela (ADR-025: un extra disponibil doar la o
 * bucatarie). E meniul contra caruia se reverifica liniile din cos.
 *
 * La RIDICARE nu exista adresa si zona: pasul 1 lipseste, iar meniul se cere
 * pentru locatia aleasa (`?ridicare=<vendor>`) — doar ce poate gati ea.
 *
 * Hook-ul e folosit de pagina de meniu si de cos: doua implementari ale
 * aceleiasi secvente ar fi putut fi de acord pe meniu si in dezacord in cos.
 */

import { useEffect, useRef } from "react";

import { CORE_ERROR_CODES, type PickupPointDto, type StorefrontAddressResponse, type StorefrontDto } from "@deliveryapp/shared";

import { ApiError, checkStorefrontAddress, fetchStorefront } from "../api/client";
import { useBootstrap } from "./BootstrapProvider";
import { fulfillmentKeyOf, useCart } from "./CartProvider";
import { revalidateCartLines, type RevalidationResult } from "./offer-refresh";
import { useLoadable } from "./useLoadable";

export interface AddressMenuState {
  /** Adresa sau locatia de ridicare contra careia s-a verificat cosul. */
  addressKey: string | null;
  /** Locatia de ridicare aleasa; `null` = livrare. */
  pickup: PickupPointDto | null;
  previewState: "idle" | "loading" | "ready" | "error";
  preview: StorefrontAddressResponse | null;
  previewError: ApiError | null;
  /**
   * Serverul a spus „nu livram acolo" — sau, la ridicare, „locatia nu primeste
   * acum comenzi de ridicare" (nu o defectiune).
   */
  outOfCoverage: boolean;
  menuState: "idle" | "loading" | "ready" | "error";
  menu: StorefrontDto | null;
  menuError: ApiError | null;
  reload: () => void;
}

export function isOutOfCoverageError(error: ApiError | null): boolean {
  return (
    error !== null &&
    (error.code === CORE_ERROR_CODES.ADDRESS_OUT_OF_COVERAGE || error.code === CORE_ERROR_CODES.NO_VENDOR_AVAILABLE)
  );
}

export function useAddressMenu(slug: string | null, enabled = true): AddressMenuState {
  const { marketKey } = useBootstrap();
  const { session } = useCart();
  const pickup = session.pickup;
  const address = pickup === null ? session.address : null;
  const addressKey = fulfillmentKeyOf(session);
  const hasSlug = enabled && slug !== null && slug.length > 0;
  const active = hasSlug && address !== null;
  const pickupActive = hasSlug && pickup !== null;

  const preview = useLoadable<{ addressKey: string; value: StorefrontAddressResponse } | null>(
    (signal) => {
      if (!active || address === null || slug === null || addressKey === null) return Promise.resolve(null);
      return checkStorefrontAddress(marketKey, slug, { lat: address.lat, lng: address.lng }, signal).then((value) => ({
        addressKey,
        value,
      }));
    },
    [marketKey, slug, active, addressKey],
  );
  const previewSnapshot = addressKey !== null && preview.data?.addressKey === addressKey ? preview.data.value : null;
  const zoneId = previewSnapshot?.zone.zoneId ?? null;

  const menu = useLoadable<{ addressKey: string; value: StorefrontDto } | null>(
    (signal) => {
      if (slug === null || addressKey === null) return Promise.resolve(null);
      if (pickupActive && pickup !== null) {
        return fetchStorefront(marketKey, slug, signal, { pickupVendorId: pickup.vendorId }).then((value) => ({ addressKey, value }));
      }
      if (!active || address === null || zoneId === null) return Promise.resolve(null);
      return fetchStorefront(marketKey, slug, signal, { zoneId, lat: address.lat, lng: address.lng }).then((value) => ({
        addressKey,
        value,
      }));
    },
    [marketKey, slug, active, pickupActive, addressKey, zoneId],
  );
  const menuSnapshot = addressKey !== null && menu.data?.addressKey === addressKey ? menu.data.value : null;

  const reload = preview.reload;
  const reloadMenu = menu.reload;

  if (pickupActive) {
    return {
      addressKey,
      pickup,
      // Ridicarea nu are previzualizare pe zona: locatia e chiar alegerea.
      previewState: "ready",
      preview: null,
      previewError: null,
      outOfCoverage: menu.error !== null && menu.error.code === CORE_ERROR_CODES.NO_VENDOR_AVAILABLE,
      menuState: menu.state === "ready" && menuSnapshot === null ? "loading" : menu.state,
      menu: menuSnapshot,
      menuError: menu.error,
      reload: reloadMenu,
    };
  }

  return {
    addressKey,
    pickup: null,
    previewState: !active ? "idle" : preview.state === "ready" && previewSnapshot === null ? "loading" : preview.state,
    preview: previewSnapshot,
    previewError: preview.error,
    outOfCoverage: isOutOfCoverageError(preview.error),
    menuState:
      !active || zoneId === null
        ? preview.state === "error"
          ? "error"
          : "idle"
        : menu.state === "ready" && menuSnapshot === null
          ? "loading"
          : menu.state,
    menu: menuSnapshot,
    menuError: menu.error,
    reload: () => {
      reload();
      reloadMenu();
    },
  };
}

/**
 * Reverifica liniile neverificate pentru adresa curenta imediat ce meniul
 * adresei e disponibil, si anunta rezultatul o singura data per adresa.
 *
 * Bucla nu se poate inchide: `revalidateCartLines` scrie
 * `verifiedForAddressKey` pe fiecare linie, deci la urmatoarea randare nu mai
 * exista linii de verificat pentru aceeasi cheie.
 */
export function useCartRevalidation(
  addressMenu: AddressMenuState,
  onResult?: (result: RevalidationResult) => void,
): void {
  const { session, replaceLines } = useCart();
  const callbackRef = useRef(onResult);
  callbackRef.current = onResult;
  const lines = session.lines;
  const menu = addressMenu.menu;
  const addressKey = addressMenu.addressKey;

  useEffect(() => {
    if (menu === null || addressKey === null) return;
    if (!lines.some((line) => line.verifiedForAddressKey !== addressKey)) return;
    const result = revalidateCartLines(lines, menu.menus, addressKey);
    replaceLines(result.lines);
    callbackRef.current?.(result);
  }, [menu, addressKey, lines, replaceLines]);
}
